import React, { useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { BookOpen, MessageSquare, RefreshCw } from "lucide-react";
import ErrorPageLayout from "@/components/common/ErrorPageLayout";
import { isBrowser } from "@/utils/runtime";

interface ServerErrorProps {
  error?: Error | null;
  onRetry?: () => void;
}

const ServerError: React.FC<ServerErrorProps> = ({ error, onRetry }) => {
  useEffect(() => {
    if (!isBrowser) return;

    globalThis.document.title = "Erro interno | Toc Toc";

    const metaDescription = globalThis.document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "Encontramos um erro temporário na Toc Toc. Tente novamente em instantes ou fale com nosso time para continuar sua jornada."
      );
    }

    let metaRobots = globalThis.document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = globalThis.document.createElement("meta");
      metaRobots.setAttribute("name", "robots");
      globalThis.document.head.appendChild(metaRobots);
    }
    metaRobots.setAttribute("content", "noindex, follow");
  }, []);

  const handleRetry = useCallback(() => {
    if (onRetry) {
      onRetry();
    } else if (isBrowser) {
      globalThis.location.reload();
    }
  }, [onRetry]);

  return (
    <ErrorPageLayout
      statusCode="500"
      title="Ops! Algo saiu do roteiro"
      description="Detectamos um comportamento inesperado na plataforma. Nossa equipe já foi notificada e estamos cuidando disso."
      details="Enquanto isso, você pode tentar novamente ou seguir pelos canais oficiais da Toc Toc."
      primaryAction={{
        label: "Tentar novamente",
        onClick: handleRetry,
        icon: <RefreshCw className="h-4 w-4" aria-hidden="true" />,
      }}
      secondaryAction={{
        label: "Voltar para a página inicial",
        to: "/",
      }}
    >
      {error?.message ? (
        <details className="mx-auto w-full max-w-xl overflow-hidden rounded-3xl border border-toctoc-orange/30 bg-white/80 p-6 text-left shadow-lg shadow-toctoc-orange/5">
          <summary className="cursor-pointer text-sm font-semibold text-toctoc-black">
            Ver detalhes técnicos
          </summary>
          <code className="mt-4 block whitespace-pre-line break-words text-xs text-toctoc-gray-medium">
            {error.message}
          </code>
        </details>
      ) : null}

      <div className="mt-12 grid gap-6 md:grid-cols-2">
        <a
          href="https://wa.me/5547997367396"
          target="_blank"
          rel="noopener noreferrer"
          className="group flex h-full flex-col justify-between rounded-3xl border border-white/40 bg-white/90 p-6 text-left shadow-xl shadow-toctoc-orange/10 transition-transform hover:-translate-y-1 hover:border-toctoc-orange/40"
        >
          <div>
            <span className="inline-flex items-center justify-center rounded-full bg-toctoc-orange/15 p-3 text-toctoc-orange">
              <MessageSquare className="h-6 w-6" aria-hidden="true" />
            </span>
            <h2 className="mt-4 text-xl font-semibold text-toctoc-black">Fale com a Toc Toc</h2>
            <p className="mt-2 text-sm text-toctoc-gray-medium">
              Abra uma conversa no WhatsApp para falar com o nosso time agora mesmo.
            </p>
          </div>
          <span className="mt-6 text-sm font-semibold text-toctoc-orange">Iniciar conversa →</span>
        </a>

        <Link
          to="/centraldeajuda"
          className="group flex h-full flex-col justify-between rounded-3xl border border-white/40 bg-white/90 p-6 text-left shadow-xl shadow-toctoc-blue/10 transition-transform hover:-translate-y-1 hover:border-toctoc-blue/40"
        >
          <div>
            <span className="inline-flex items-center justify-center rounded-full bg-toctoc-blue/15 p-3 text-toctoc-blue">
              <BookOpen className="h-6 w-6" aria-hidden="true" />
            </span>
            <h2 className="mt-4 text-xl font-semibold text-toctoc-black">Central de ajuda</h2>
            <p className="mt-2 text-sm text-toctoc-gray-medium">
              Consulte guias e respostas rápidas sobre nossos serviços, entregas e suporte.
            </p>
          </div>
          <span className="mt-6 text-sm font-semibold text-toctoc-blue">Acessar artigos →</span>
        </Link>
      </div>
    </ErrorPageLayout>
  );
};

export default ServerError;
